import type { IconType } from '../components/various/Icon/Icon';

/**
 * Kreatli features confirmed in code (wave 2 plan, Oct 2026). Marketing pages build their capability grids from
 * this list so they never claim a feature that does not exist.
 *
 * Not available, never claim: comment export, website/URL proofing, white label, Google Drive/Dropbox
 * integrations, NLE panels/plugins, frame stepping. Say "timecoded comments", not "frame-accurate".
 */
export type KreatliCapabilityId =
  | 'fileTypes'
  | 'timecodedComments'
  | 'drawnMarkup'
  | 'pdfPageComments'
  | 'threadedComments'
  | 'versionCompare'
  | 'approvalStatuses'
  | 'guestReview'
  | 'shareControls'
  | 'tasks'
  | 'deliverables'
  | 'chat';

export interface KreatliCapability {
  title: string;
  description: string;
  icon: IconType;
}

export const KREATLI_CAPABILITIES: Record<KreatliCapabilityId, KreatliCapability> = {
  fileTypes: {
    title: 'Video, image, PDF and audio review',
    description: 'Proof cuts, stills, layouts and audio in one project instead of a different tool per format.',
    icon: 'monitorPlay',
  },
  timecodedComments: {
    title: 'Timecoded and time-range comments',
    description: 'Pin a note to a timecode or a range of the timeline so the editor jumps straight to the moment.',
    icon: 'time',
  },
  drawnMarkup: {
    title: 'Drawn markup',
    description: 'Circle, arrow or sketch on a frame or image to show exactly what needs to change.',
    icon: 'paint',
  },
  pdfPageComments: {
    title: 'PDF page comments',
    description: 'Leave comments on a specific page of a PDF, deck, brochure or storyboard.',
    icon: 'filePdf',
  },
  threadedComments: {
    title: 'Threaded, resolvable comments',
    description: 'Reply in a thread under each note and resolve it once the change is made.',
    icon: 'chat',
  },
  versionCompare: {
    title: 'Version stacks and version compare',
    description: 'Upload each round onto the same asset and compare two versions side by side.',
    icon: 'versions',
  },
  approvalStatuses: {
    title: 'Custom approval statuses',
    description: 'Define your own statuses, such as "In review" or "Approved". Guests see them on the review link.',
    icon: 'checkCircle',
  },
  guestReview: {
    title: 'Guest review without login',
    description: 'Reviewers open a link and comment after entering their name. No account and no paid seat.',
    icon: 'link',
  },
  shareControls: {
    title: 'Share-link download and edit controls',
    description: 'Choose per link whether guests can download the file or make edits.',
    icon: 'share',
  },
  tasks: {
    title: 'Tasks on boards',
    description: 'Create tasks with an owner and contributors, link them to files and move them through board columns.',
    icon: 'board',
  },
  deliverables: {
    title: 'Deliverables',
    description: 'List what the project has to ship, with owners and due dates, next to the files themselves.',
    icon: 'calendar',
  },
  chat: {
    title: 'Project chat',
    description: 'Talk about the work in the project instead of in email threads that lose the file.',
    icon: 'reply',
  },
};

/** Capabilities in the given order, with optional page-specific descriptions. */
export function getKreatliCapabilities(
  ids: KreatliCapabilityId[],
  descriptions: Partial<Record<KreatliCapabilityId, string>> = {},
): KreatliCapability[] {
  return ids.map((id) => ({
    ...KREATLI_CAPABILITIES[id],
    description: descriptions[id] ?? KREATLI_CAPABILITIES[id].description,
  }));
}
